import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { useState } from "react";
import LogDetail from "./LogDetail";

const formatMethod = (method) => {
    if (!method) return "—";
    return method
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const LogRow = ({ log }) => {
    const [open, setOpen] = useState(false);
    const formattedTime = dayjs(log.datetime).format("YYYY-MM-DD HH:mm:ss");

    const toggleDetails = () => setOpen(!open);

    return (
        <>
            <StyledTableRow>
                <TableCell
                    padding="checkbox"
                    width={"48px"}
                    sx={{ width: "48px", maxWidth: "48px" }}
                >
                    <IconButton
                        size="small"
                        onClick={toggleDetails}
                        aria-label={
                            open ? "collapse details" : "expand details"
                        }
                    >
                        {open ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>
                </TableCell>
                <StyledTableCell>
                    <Box direction="row" alignItems="center" spacing={2}>
                        {formattedTime}
                    </Box>
                </StyledTableCell>
                <StyledTableCell>{formatMethod(log.method)}</StyledTableCell>
                <TableCell>
                    <StatusIndicator status={log.error ? "error" : "success"}>
                        {log.error ? "Error" : "Success"}
                    </StatusIndicator>
                </TableCell>
            </StyledTableRow>

            <TableRow
                sx={{
                    "& > td": {
                        borderBottom: "none",
                        padding: 0,
                    },
                    backgroundColor: (theme) =>
                        open
                            ? alpha(theme.palette.primary.main, 0.03)
                            : "transparent",
                }}
            >
                <TableCell style={{ padding: 0 }} colSpan={4}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card
                            variant="outlined"
                            sx={{
                                m: 2,
                                border: "none",
                                bgcolor: "background.paper",
                            }}
                        >
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 600,
                                        color: "primary.main",
                                    }}
                                >
                                    Log Details
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <LogDetail label="ID" value={log._id} />
                                {log.kwargs && (
                                    <LogDetail
                                        label="Parameters"
                                        value={log.kwargs}
                                    />
                                )}
                                {log.input && (
                                    <LogDetail
                                        label="Input"
                                        value={log.input}
                                    />
                                )}
                                {log.args && log.args.length > 0 && (
                                    <LogDetail
                                        label="Arguments"
                                        value={log.args}
                                    />
                                )}
                                {log.sfs_method && (
                                    <LogDetail
                                        label="SFS Method"
                                        value={log.sfs_method}
                                    />
                                )}
                                {log.error && (
                                    <LogDetail
                                        label="Error"
                                        value={log.error}
                                        isError={true}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default LogRow;

const StatusIndicator = styled(Box)(({ theme, status }) => ({
    backgroundColor:
        status === "error"
            ? theme.palette.error.main
            : theme.palette.success.main,
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: "4px 0",
    display: "block",
    fontWeight: 500,
    fontSize: "0.75rem",
    textAlign: "center",
    width: "100%",
    maxWidth: 100,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:not(:last-child)": {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& > td": {
        padding: theme.spacing(1.5),
        whiteSpace: "nowrap",
        borderBottom: "none",
    },
    "&:nth-of-type(odd)": {
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
    },
    "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
}));

const StyledTableCell = styled(TableCell)(() => ({
    fontWeight: 500,
    borderBottom: "none",
}));
